import { useEffect, useRef, useState } from "react";
import { Plus, FileSignature, Send, ShieldCheck, X, Eye, Ban, FileText, Receipt, Mail, MessageCircle, UserPlus } from "lucide-react";
import api, { API_BASE } from "@/lib/api";
import { PageHeader, StatusChip, EmptyState } from "@/components/Common";
import { IdentificationField, sriExtraToFields, SriStatusBanner } from "@/pages/Customers";
import { toast } from "sonner";

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = () => api.get("/invoices").then((r) => setInvoices(r.data));
  useEffect(() => {
    load();
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/customers").then((r) => setCustomers(r.data));
    api.get("/promotions").then((r) => setPromotions(r.data));
  }, []);

  const action = async (id, path) => {
    try {
      const { data } = await api.post(`/invoices/${id}/${path}`);
      toast.success(`Operación '${path}' completada.`);
      load();
      if (detail && detail.id === id) setDetail(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div data-testid="invoices-page">
      <PageHeader
        title="Facturación electrónica SRI"
        subtitle="Emite, firma y envía facturas al SRI Ecuador."
        testid="invoices-header"
        action={
          <button data-testid="invoices-create-button" onClick={() => setShowForm(true)} className="btn-primary flex items-center gap-2">
            <Plus className="w-4 h-4" /> Nueva factura
          </button>
        }
      />

      {showForm && (
        <InvoiceForm
          customers={customers}
          products={products}
          promotions={promotions}
          onClose={() => setShowForm(false)}
          onCreated={() => { setShowForm(false); load(); }}
        />
      )}

      {invoices.length === 0 ? (
        <EmptyState title="Sin facturas" hint="Crea tu primera factura electrónica." testid="invoices-empty" />
      ) : (
        <div className="card-soft overflow-x-auto">
          <table className="w-full text-sm" data-testid="invoices-table">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-[0.18em] text-[#8A9A92]">
                <th className="px-5 py-3">Número</th><th className="px-5 py-3">Cliente</th>
                <th className="px-5 py-3">Fecha</th><th className="px-5 py-3 text-right">Total</th>
                <th className="px-5 py-3">Estado</th><th className="px-5 py-3 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => (
                <tr key={inv.id} className="border-t border-[#E2E0D8] hover:bg-[#FAFAF8]">
                  <td className="px-5 py-4 font-mono">{inv.document_number}</td>
                  <td className="px-5 py-4 font-medium">{inv.customer_name}</td>
                  <td className="px-5 py-4 text-xs text-[#5C6B64]">{new Date(inv.issue_date).toLocaleString()}</td>
                  <td className="px-5 py-4 text-right font-bold">$ {Number(inv.total).toFixed(2)}</td>
                  <td className="px-5 py-4"><StatusChip status={inv.status} /></td>
                  <td className="px-5 py-4 text-right">
                    <div className="inline-flex gap-1">
                      <button title="Ver" onClick={() => setDetail(inv)} className="p-2 rounded hover:bg-[#F3F2ED]"><Eye className="w-4 h-4 text-[#5C6B64]" /></button>
                      <a title="Descargar Factura A4" href={`${API_BASE}/invoices/${inv.id}/pdf`} target="_blank" rel="noreferrer"
                        className="p-2 rounded hover:bg-[#F3F2ED] inline-flex" data-testid={`inv-pdf-a4-${inv.id}`}><FileText className="w-4 h-4 text-[#5C6B64]" /></a>
                      <a title="Imprimir Ticket 76mm" href={`${API_BASE}/invoices/${inv.id}/pdf?format=ticket`} target="_blank" rel="noreferrer"
                        className="p-2 rounded hover:bg-[#F3F2ED] inline-flex" data-testid={`inv-pdf-ticket-${inv.id}`}><Receipt className="w-4 h-4 text-[#5C6B64]" /></a>
                      {inv.customer_email && (
                        <button title="Enviar por email" onClick={async () => {
                          try { const { data } = await api.post(`/invoices/${inv.id}/email`); toast.success(`Email enviado a ${data.to}`); }
                          catch (e) { toast.error(e.response?.data?.detail || "Error"); }
                        }} className="p-2 rounded hover:bg-[#DDE6ED]"><Mail className="w-4 h-4 text-[#608090]" /></button>
                      )}
                      {inv.customer_phone && (() => {
                        const phone = (inv.customer_phone || "").replace(/\D/g, "");
                        const full = phone.startsWith("593") ? phone : `593${phone.replace(/^0/, "")}`;
                        const text = encodeURIComponent(`Hola ${inv.customer_name}, te enviamos tu factura electrónica ${inv.document_number} por $ ${Number(inv.total).toFixed(2)}. Descárgala aquí: ${API_BASE}/invoices/${inv.id}/pdf`);
                        return (
                          <a title="Enviar por WhatsApp" href={`https://wa.me/${full}?text=${text}`} target="_blank" rel="noreferrer" className="p-2 rounded hover:bg-[#E4EFE6] inline-flex"><MessageCircle className="w-4 h-4 text-[#507A59]" /></a>
                        );
                      })()}
                      {inv.status === "borrador" && (
                        <button title="Firmar" onClick={() => action(inv.id, "sign")} className="p-2 rounded hover:bg-[#DDE6ED]"><FileSignature className="w-4 h-4 text-[#608090]" /></button>
                      )}
                      {inv.status === "firmada" || inv.status === "rechazada" ? (
                        <button title="Enviar" onClick={() => action(inv.id, "send")} className="p-2 rounded hover:bg-[#DDE6ED]"><Send className="w-4 h-4 text-[#608090]" /></button>
                      ) : null}
                      {(inv.status === "enviada" || inv.status === "firmada" || inv.status === "rechazada") && (
                        <button title="Autorizar" onClick={() => action(inv.id, "authorize")} className="p-2 rounded hover:bg-[#E4EFE6]"><ShieldCheck className="w-4 h-4 text-[#507A59]" /></button>
                      )}
                      {inv.status !== "anulada" && (
                        <button title="Anular" onClick={() => { if (window.confirm("¿Anular factura?")) action(inv.id, "cancel"); }} className="p-2 rounded hover:bg-[#F3DADA]"><Ban className="w-4 h-4 text-[#A44A4A]" /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {detail && <InvoiceDetail invoice={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

function InvoiceForm({ customers, products, promotions, onClose, onCreated }) {
  const uidRef = useRef(0);
  const newRow = () => ({ uid: ++uidRef.current, product_id: "", quantity: 1, unit_price: "", discount: 0, promotion_id: "" });
  const [customer_id, setCustomerId] = useState("");
  const [items, setItems] = useState(() => [newRow()]);
  const [payment_method, setPayment] = useState("efectivo");
  const [saving, setSaving] = useState(false);
  const [showQuickCustomer, setShowQuickCustomer] = useState(false);
  const [customerList, setCustomerList] = useState(customers);
  const selectedCustomer = customerList.find((c) => c.id === customer_id) || null;

  useEffect(() => { setCustomerList(customers); }, [customers]);

  const productMap = Object.fromEntries(products.map((p) => [p.id, p]));

  const setItem = (i, patch) => {
    const next = items.slice();
    next[i] = { ...next[i], ...patch };
    setItems(next);
  };
  const addRow = () => setItems([...items, newRow()]);
  const rmRow = (i) => setItems(items.filter((_, idx) => idx !== i));

  const totals = items.reduce((acc, it) => {
    const p = productMap[it.product_id];
    if (!p) return acc;
    const up = it.unit_price !== "" ? Number(it.unit_price) : Number(p.sale_price);
    const sub = up * Number(it.quantity || 0) - Number(it.discount || 0);
    const iva = Math.max(0, sub) * Number(p.iva_rate || 0);
    acc.subtotal += Math.max(0, sub);
    acc.iva += iva;
    acc.total += Math.max(0, sub) + iva;
    return acc;
  }, { subtotal: 0, iva: 0, total: 0 });

  const submit = async (e) => {
    e.preventDefault();
    if (!customer_id) return toast.error("Seleccione un cliente");
    if (items.some((i) => !i.product_id)) return toast.error("Complete todos los productos");
    setSaving(true);
    try {
      await api.post("/invoices", {
        customer_id,
        payment_method,
        items: items.map((i) => ({
          product_id: i.product_id,
          quantity: Number(i.quantity),
          unit_price: i.unit_price !== "" ? Number(i.unit_price) : undefined,
          discount: Number(i.discount) || 0,
          promotion_id: i.promotion_id || undefined,
        })),
      });
      toast.success("Factura creada en estado borrador");
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="invoice-form" className="card-soft p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-xl font-bold">Nueva factura</h3>
        <button onClick={onClose} className="text-[#8A9A92] hover:text-[#2A3F35]"><X className="w-5 h-5" /></button>
      </div>
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-[#8A9A92] mb-1.5 block">Cliente *</span>
            <div className="flex gap-2">
              <select data-testid="invoice-customer-select" required className="input-soft flex-1" value={customer_id} onChange={(e) => setCustomerId(e.target.value)}>
                <option value="">Seleccione cliente</option>
                {customerList.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.identification})</option>)}
              </select>
              <button type="button" onClick={() => setShowQuickCustomer(true)}
                title="Crear cliente nuevo por cédula/RUC"
                data-testid="invoice-quick-customer-button"
                className="btn-secondary px-3 flex items-center gap-1 whitespace-nowrap">
                <UserPlus className="w-4 h-4" /> Nuevo
              </button>
            </div>
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-[#8A9A92] mb-1.5 block">Forma de pago</span>
            <select className="input-soft" value={payment_method} onChange={(e) => setPayment(e.target.value)}>
              <option value="efectivo">Efectivo</option>
              <option value="tarjeta">Tarjeta</option>
              <option value="transferencia">Transferencia</option>
              <option value="credito">Crédito</option>
            </select>
          </label>
        </div>

        <div className="border border-[#E2E0D8] rounded-xl overflow-hidden">
          <div className="grid grid-cols-12 gap-2 p-3 bg-[#F3F2ED] text-[11px] uppercase tracking-[0.16em] text-[#5C6B64]">
            <div className="col-span-4">Producto</div>
            <div className="col-span-2">Cantidad</div>
            <div className="col-span-2">Precio (sin IVA)</div>
            <div className="col-span-2">Descuento</div>
            <div className="col-span-2">Promoción</div>
          </div>
          {items.map((it, idx) => (
            <div key={it.uid} className="grid grid-cols-12 gap-2 p-3 border-t border-[#E2E0D8] items-center">
              <select className="input-soft col-span-4" value={it.product_id} onChange={(e) => setItem(idx, { product_id: e.target.value, unit_price: "" })}>
                <option value="">Seleccione</option>
                {products.map((p) => <option key={p.id} value={p.id}>{p.name} – $ {Number(p.sale_price).toFixed(2)} (stock {Number(p.stock || 0).toFixed(2)})</option>)}
              </select>
              <input type="number" step="0.01" className="input-soft col-span-2" value={it.quantity} onChange={(e) => setItem(idx, { quantity: e.target.value })} />
              <input type="number" step="0.01" placeholder={productMap[it.product_id]?.sale_price?.toFixed?.(2) || ""} className="input-soft col-span-2" value={it.unit_price} onChange={(e) => setItem(idx, { unit_price: e.target.value })} />
              <input type="number" step="0.01" className="input-soft col-span-2" value={it.discount} onChange={(e) => setItem(idx, { discount: e.target.value })} />
              <div className="col-span-2 flex items-center gap-1">
                <select className="input-soft flex-1" value={it.promotion_id} onChange={(e) => setItem(idx, { promotion_id: e.target.value })}>
                  <option value="">—</option>
                  {promotions.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                {items.length > 1 && <button type="button" onClick={() => rmRow(idx)} className="p-2 rounded hover:bg-[#F3DADA]"><X className="w-4 h-4 text-[#A44A4A]" /></button>}
              </div>
            </div>
          ))}
          <div className="p-3 border-t border-[#E2E0D8] bg-[#FAFAF8]">
            <button type="button" onClick={addRow} className="btn-secondary text-sm flex items-center gap-2"><Plus className="w-4 h-4" /> Agregar línea</button>
          </div>
        </div>

        <div className="flex flex-col md:flex-row md:items-center md:justify-end gap-2 text-sm">
          <div className="md:mr-6"><span className="text-[#8A9A92] mr-2">Subtotal:</span><strong>$ {totals.subtotal.toFixed(2)}</strong></div>
          <div className="md:mr-6"><span className="text-[#8A9A92] mr-2">IVA:</span><strong>$ {totals.iva.toFixed(2)}</strong></div>
          <div className="font-heading text-2xl font-black">Total: $ {totals.total.toFixed(2)}</div>
        </div>

        {selectedCustomer && (
          <SriStatusBanner
            customer={selectedCustomer}
            onRevalidated={(updated) => {
              setCustomerList((prev) => prev.map((c) => c.id === updated.id ? updated : c));
            }}
          />
        )}

        <div className="flex justify-end gap-3">
          <button type="button" className="btn-secondary" onClick={onClose}>Cancelar</button>
          <button data-testid="invoice-save-button" disabled={saving} className="btn-primary" type="submit">{saving ? "Guardando..." : "Crear factura"}</button>
        </div>
      </form>
      {showQuickCustomer && (
        <QuickCustomerModal
          onClose={() => setShowQuickCustomer(false)}
          onCreated={(newCustomer) => {
            setCustomerList((prev) => [...prev, newCustomer]);
            setCustomerId(newCustomer.id);
            setShowQuickCustomer(false);
          }}
        />
      )}
    </div>
  );
}

function QuickCustomerModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    identification_type: "cedula", identification: "", name: "",
    email: "", phone: "", address: "", city: "", active: true,
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.identification || !form.name) {
      toast.error("Identificación y nombre son obligatorios");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/customers", form);
      toast.success("Cliente creado y seleccionado");
      onCreated(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al crear cliente");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/45 grid place-items-center p-4" data-testid="quick-customer-modal">
      <form onSubmit={submit} className="bg-white rounded-2xl max-w-xl w-full p-7 border border-[#E2E0D8] relative">
        <button type="button" onClick={onClose} className="absolute top-4 right-4 text-[#8A9A92] hover:text-[#2A3F35]" data-testid="quick-customer-close"><X className="w-5 h-5" /></button>
        <p className="text-[11px] uppercase tracking-[0.22em] text-[#8A9A92]">Nuevo cliente</p>
        <h3 className="font-heading text-2xl font-extrabold mb-1">Cliente para factura</h3>
        <p className="text-sm text-[#5C6B64] mb-5">Ingresa la cédula o RUC: los demás campos se autocompletan desde el SRI.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-[#8A9A92] mb-1.5 block">Tipo *</span>
            <select className="input-soft" value={form.identification_type}
              onChange={(e) => setForm({
                ...form, identification_type: e.target.value, identification: "",
                tipo_contribuyente: null, obligado_contabilidad: null,
                contribuyente_especial: null, agente_retencion: null,
                actividad_economica: null, regimen: null, estado_sri: null, sri_synced_at: null,
              })}>
              <option value="cedula">Cédula</option>
              <option value="ruc">RUC</option>
              <option value="pasaporte">Pasaporte</option>
              <option value="consumidor_final">Consumidor Final</option>
            </select>
          </label>
          <IdentificationField
            testid="quick-customer-identification"
            type={form.identification_type}
            value={form.identification}
            onChange={(val) => setForm((f) => ({ ...f, identification: val }))}
            onFound={(d) => setForm((f) => ({
              ...f,
              name: d.name || f.name,
              address: d.address || f.address,
              phone: d.phone || f.phone,
              email: d.email || f.email,
              ...sriExtraToFields(d),
            }))}
          />
          <label className="block md:col-span-2">
            <span className="text-xs uppercase tracking-[0.18em] text-[#8A9A92] mb-1.5 block">Nombre / Razón social *</span>
            <input data-testid="quick-customer-name" required className="input-soft" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-[#8A9A92] mb-1.5 block">Email</span>
            <input type="email" className="input-soft" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-[#8A9A92] mb-1.5 block">Teléfono</span>
            <input className="input-soft" value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </label>
          <label className="block md:col-span-2">
            <span className="text-xs uppercase tracking-[0.18em] text-[#8A9A92] mb-1.5 block">Dirección</span>
            <input className="input-soft" value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })} />
          </label>
        </div>
        <div className="flex justify-end gap-3 mt-6">
          <button type="button" className="btn-secondary" onClick={onClose}>Cancelar</button>
          <button data-testid="quick-customer-save" disabled={saving} className="btn-primary" type="submit">
            {saving ? "Guardando..." : "Crear y seleccionar"}
          </button>
        </div>
      </form>
    </div>
  );
}

function InvoiceDetail({ invoice, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/30 grid place-items-center p-4" data-testid="invoice-detail-modal">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-7 border border-[#E2E0D8]">
        <div className="flex items-start justify-between mb-5">
          <div>
            <p className="text-xs uppercase tracking-[0.22em] text-[#8A9A92]">Factura</p>
            <h3 className="font-heading text-2xl font-black">{invoice.document_number}</h3>
            <p className="text-sm text-[#5C6B64] mt-1">{new Date(invoice.issue_date).toLocaleString()}</p>
          </div>
          <button onClick={onClose} className="text-[#8A9A92] hover:text-[#2A3F35]"><X className="w-5 h-5" /></button>
        </div>
        <div className="mb-4"><StatusChip status={invoice.status} /></div>
        <div className="grid grid-cols-2 gap-4 text-sm mb-5">
          <div><p className="text-[11px] uppercase tracking-wider text-[#8A9A92]">Cliente</p><p className="font-medium">{invoice.customer_name}</p><p className="font-mono text-xs">{invoice.customer_identification}</p></div>
          <div><p className="text-[11px] uppercase tracking-wider text-[#8A9A92]">Forma de pago</p><p className="capitalize">{invoice.payment_method}</p></div>
        </div>
        {invoice.access_key && (
          <div className="mb-5"><p className="text-[11px] uppercase tracking-wider text-[#8A9A92]">Clave de acceso (49 dígitos)</p><p className="font-mono text-xs break-all">{invoice.access_key}</p></div>
        )}
        {invoice.authorization_number && (
          <div className="mb-5"><p className="text-[11px] uppercase tracking-wider text-[#8A9A92]">N° autorización</p><p className="font-mono text-xs break-all">{invoice.authorization_number}</p></div>
        )}
        <table className="w-full text-sm mb-5">
          <thead><tr className="text-left text-[11px] uppercase tracking-[0.16em] text-[#8A9A92]"><th className="py-2">Producto</th><th>Cant.</th><th>Precio</th><th>Subtotal</th></tr></thead>
          <tbody>
            {invoice.items.map((it, i) => (
              <tr key={i} className="border-t border-[#E2E0D8]"><td className="py-2">{it.name}</td><td>{it.quantity}</td><td>$ {it.unit_price.toFixed(2)}</td><td>$ {it.subtotal.toFixed(2)}</td></tr>
            ))}
          </tbody>
        </table>
        <div className="text-right text-sm space-y-1">
          <div>Subtotal 15%: <strong>$ {Number(invoice.subtotal_15).toFixed(2)}</strong></div>
          <div>Subtotal 0%: <strong>$ {Number(invoice.subtotal_0).toFixed(2)}</strong></div>
          <div>Descuento: <strong>$ {Number(invoice.discount_total).toFixed(2)}</strong></div>
          <div>IVA: <strong>$ {Number(invoice.iva_total).toFixed(2)}</strong></div>
          <div className="font-heading text-2xl font-black mt-2">Total: $ {Number(invoice.total).toFixed(2)}</div>
        </div>
        {invoice.sri_message && (
          <div className="mt-5 p-4 bg-[#F3F2ED] border border-[#E2E0D8] rounded-lg text-sm">
            <p className="text-[11px] uppercase tracking-wider text-[#8A9A92] mb-1">Mensaje SRI</p>
            <p>{invoice.sri_message}</p>
          </div>
        )}
      </div>
    </div>
  );
}
